import React, {
  createContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import * as authApi from "../api/authApi";

export const AuthContext = createContext(null);

export default function AuthProvider({ children }) {
  const [parent, setParent] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const beforeLogoutRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await authApi.bootstrapAuth();
        setParent(data?.parent ?? null);
      } catch {
        setParent(null);
      } finally {
        setIsBootstrapping(false);
      }
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    await authApi.login(email, password);

    try {
      const data = await authApi.bootstrapAuth();
      setParent(data?.parent ?? null);
      return data;
    } catch (err) {
      console.warn("Bootstrap failed, but login likely succeeded", err);
      setParent(null);
      return null;
    }
  }, []);

  const register = useCallback(async (email, password, birth_date) => {
    await authApi.register(email, password, birth_date);

    const data = await authApi.bootstrapAuth();
    setParent(data?.parent ?? null);

    return data;
  }, []);

  const registerBeforeLogout = useCallback((fn) => {
    beforeLogoutRef.current = fn;
  }, []);

  const clearBeforeLogout = useCallback(() => {
    beforeLogoutRef.current = null;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (beforeLogoutRef.current) {
        await beforeLogoutRef.current();
      }

      await authApi.logout();
    } finally {
      setParent(null);
      beforeLogoutRef.current = null;
    }
  }, []);

  const isAuthed = !!parent;

  const value = useMemo(
    () => ({
      parent,
      isAuthed,
      isBootstrapping,
      login,
      register,
      logout,
      setParent,
      registerBeforeLogout,
      clearBeforeLogout,
    }),
    [
      parent,
      isAuthed,
      isBootstrapping,
      login,
      register,
      logout,
      registerBeforeLogout,
      clearBeforeLogout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}